import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useQuestions from '@/hooks/useQuestions';
import useReviewInformation from '@/hooks/useReviewInformation';
import { ApplicationAnswerProps } from '@/models/Application';
import { QuestionProps } from '@/models/Question';
import { QuestionReviewProps } from '@/models/QuestionReview';

import CommentBlock from './CommentBlock';
import { logError } from '@/utils/errorHandlerUtil';
import { ApplicationStatus } from '@/utils/constants';

function CommentsWrap({ question }: { question: QuestionProps }) {
  const { currentContact } = useContext(UsersContext);

  const { answers, currentApplication, updateAnswers } = useContext(CurrentApplicationContext);
  const { setAlert } = useContext(SnackbarContext);
  const { getSpecificQuestionAnswer } = useQuestions();
  const { postReviewerComment, postQuestionReviewResolved } = useReviewInformation();

  const [chatClicked, setChatClicked] = useState<boolean>(false);
  const [chatContent, setChatContent] = useState<QuestionReviewProps[]>([]);

  const questionAnswer = useMemo(() => answers[question.id], [answers, question.id]);
  const commentCount = questionAnswer?.reviews.length ?? 0;

  const hasParentQuestion = useMemo(
    () => (question?.parentQuestion ?? []).length > 0,
    [question.parentQuestion]
  );

  useEffect(() => {
    if (questionAnswer != null && questionAnswer?.reviews.length > 0) {
      setChatContent(questionAnswer.reviews);
    }
  }, [questionAnswer]);

  function updateCommentsOnApplication(newReviews: QuestionReviewProps[]) {
    if (newReviews != null && questionAnswer != null && answers != null) {
      // update only the reviews field on the current question in the allQuestions array
      questionAnswer.reviews = newReviews;
      updateAnswers(questionAnswer);
    }
  }

  function toggleShowComment() {
    setChatClicked(!chatClicked);
  }

  async function addComment(newComment: string): Promise<boolean> {
    if (currentApplication.id == null) {
      logError(`Application ID could not be found`);
      return false;
    }

    if (currentContact?.id == null) {
      logError(`Current contact ID could not be found`);
      return false;
    }

    const postInfo: QuestionReviewProps = {
      questionId: question.id,
      applicationId: currentApplication.id,
      reviewerId: currentContact.id,
      reviewerNotes: newComment,
      reviewDate: dayjs().format().toString(),
      reviewStatus: currentApplication.status,
    };

    const resolved: boolean =
      questionAnswer?.reviews?.some((review: QuestionReviewProps) => review.resolved) ?? false;

    return await postReviewerComment(postInfo).then(
      async (response: QuestionReviewProps | string | null | { errors: string }) => {
        if (
          response === null ||
          typeof response === 'string' ||
          ('errors' in response && response.errors == null)
        ) {
          return false;
        } else {
          // if the comment was resolved before adding a new comment, reactivate the comment
          if (resolved) {
            await resolveComment(false);
          }

          try {
            await getSpecificQuestionAnswer(question.id).then((ans: ApplicationAnswerProps) => {
              setChatContent(ans.reviews);
              updateCommentsOnApplication(ans.reviews);
            });
          } catch (error) {
            logError(
              `Error updating the question reviews after adding a comment for question ${question.id}`,
              error
            );
          }

          return true;
        }
      }
    );
  }

  async function resolveComment(newCommentResolvedValue: boolean) {
    if (currentApplication.id == null) return;

    const response = await postQuestionReviewResolved(
      currentApplication.id,
      question.id,
      newCommentResolvedValue
    );

    if (response != null) {
      setChatContent(response);
      updateCommentsOnApplication(response);

      if (response.some((chat: QuestionReviewProps) => chat.resolved)) {
        toggleShowComment();
        setAlert({ message: 'Comment resolved!', severity: 'success' });
      }
    }
  }

  return currentApplication.status != ApplicationStatus.InProgress &&
    !hasParentQuestion &&
    currentContact != null ? (
    <CommentBlock
      id={question.id}
      toggleShowComment={toggleShowComment}
      chatClicked={chatClicked}
      chatContent={chatContent}
      addComment={addComment}
      resolveComment={resolveComment}
    />
  ) : (
    <></>
  );
}

export default CommentsWrap;
